// A paged table's place: which rows a page holds, and how far down its scroll
// panel the reader was (release 3.3, task V3 of the 7 Oct plan; Back model B6).
//
// # WHY THIS EXISTS
//
// The register pages its rows inside a scroll panel, so the rows scroll and
// the page's head, filters and pager stay. lib/scrollPlace.ts keeps the
// window's position for Back, but a panel has a position of its own, and Back
// from a vendor opened from row 20 landed on the right page at its first row.
// The panel's position is kept in the history entry's own state, beside the
// window's key and the origin, so Back and Forward each find their own.

import { clampPage, cleanSize } from '../components/kit/flow'

// The rows of one page, and the page they belong to: a page that no longer
// exists (the list shrank under a search) is the last one that does.
export function pageRows<T>(rows: readonly T[], page: number, size: number): { rows: T[]; page: number; first: number } {
  const s = cleanSize(size)
  const p = clampPage(page, rows.length, s)
  const first = (p - 1) * s
  return { rows: rows.slice(first, first + s), page: p, first }
}

// Whether a press on a row opens its record (review of V3, S1 and NIT 1). The
// row is a mouse convenience only: the record's link in the row is the one
// keyboard stop, so the row takes no focus and no Enter. A press on a control
// inside the row is that control's; a press that ends a text selection is a
// copy; a modified or non-primary press is the browser's (a new tab, say),
// which the link in the row already offers.
export function rowPressOpens(p: {
  onControl: boolean
  selecting: boolean
  button: number
  ctrl?: boolean
  meta?: boolean
  shift?: boolean
  alt?: boolean
}): boolean {
  return !p.onControl && !p.selecting && p.button === 0 && !p.ctrl && !p.meta && !p.shift && !p.alt
}

// The panel's position as a history entry's state holds it; nothing usable
// is the top.
export function panelYOf(state: unknown): number {
  const y = state && typeof state === 'object' ? (state as { panelY?: unknown }).panelY : undefined
  return typeof y === 'number' && Number.isFinite(y) && y > 0 ? Math.round(y) : 0
}

// The state with the panel's position written in, every other key kept (the
// window's key from scrollPlace.ts, the origin from origin.ts).
export function withPanelY(state: unknown, y: number): Record<string, unknown> {
  const base = state && typeof state === 'object' ? (state as Record<string, unknown>) : {}
  return { ...base, panelY: Math.max(0, Math.round(Number.isFinite(y) ? y : 0)) }
}

// Writes the position into the entry on screen.
export function keepPanelY(y: number): void {
  try {
    history.replaceState(withPanelY(history.state, y), '')
  } catch {
    // A browser that refuses history state opens the page at its first row.
  }
}
